import { Injectable, signal } from '@angular/core';
import chunk from 'lodash-es/chunk';
import { Observable, map, catchError, of, timeout, from, mergeMap, reduce } from 'rxjs';

import { MutationState, UpdateRequestResponse, UpdateResponse, UpdateState } from '@shure/cloud/shared/models/http';
import { DeviceBluetoothPower } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';

import {
	AssociateTagMutationResult,
	CloudDeviceApiService,
	DissociateTagMutationResult,
	RequestFirmwareUpdateApiInput,
	UpdateChannelAuracastApiInput
} from '../api/cloud-device-api.service';

import {
	AssociateTagMutationGQL,
	DeviceRebootMutationGQL,
	DeviceStartIdentifyMutationGQL,
	DeviceStopIdentifyMutationGQL,
	DeviceUpdateMutationGQL,
	DissociateTagMutationGQL,
	FirmwareUpdateRequestsByStatusFragment,
	FirmwareUpdateRequestState,
	SetDeviceBluetoothPowerMutationGQL,
	UpdateAuracastConfigurationMutationGQL,
	RequestFirmwareUpdatesGQL,
	UpdateDeviceIotRoomProxyFeatureGQL,
	FirmwareUpdateRequestMutationResponseFragment
} from './graphql/generated/cloud-sys-api';
import { mapDeviceBluetoothPowerToSysApi } from './mappers/map-bluetooth-power';
import { SysApiDeviceInventoryApolloCache } from './sys-api-device-inventory-apollo-cache.service';

const defaultMutationTimeout = 15_000;

@Injectable({ providedIn: 'root' })
export class SysApiCloudDeviceApiService extends CloudDeviceApiService {
	private readonly logger: ILogger;
	public override readonly lastFWUpdateTime = signal(0);

	constructor(
		logger: ILogger,
		private readonly deviceStopIdentifyMutationGQL: DeviceStopIdentifyMutationGQL,
		private readonly deviceStartIdentifyMutationGQL: DeviceStartIdentifyMutationGQL,
		private readonly deviceRebootMutationGQL: DeviceRebootMutationGQL,
		private readonly deviceUpdateMutationGQL: DeviceUpdateMutationGQL,
		private readonly associateTagMutationGQL: AssociateTagMutationGQL,
		private readonly dissociateTagMutationGQL: DissociateTagMutationGQL,
		private readonly deviceUpdateIotRoomProxyMutationGQL: UpdateDeviceIotRoomProxyFeatureGQL,
		private readonly requestFirmwareUpdates: RequestFirmwareUpdatesGQL /* FW Update V2 */,

		// Auracast Mutations
		private readonly updateAuracastConfigurationMutationGQL: UpdateAuracastConfigurationMutationGQL,
		private readonly setDeviceBluetoothPowerMutationGQL: SetDeviceBluetoothPowerMutationGQL,

		private readonly inventoryApolloCache: SysApiDeviceInventoryApolloCache
	) {
		super();

		this.logger = logger.createScopedLogger('DaiCloudDeviceService');
	}

	/**
	 * Toggle identify for a device.
	 * @param deviceId
	 * @param identify
	 * @returns
	 */
	public setIdentify(deviceId: string, identify: boolean): Observable<UpdateRequestResponse<string>> {
		this.logger.trace('toggleIdentify', 'Toggling identify', { deviceId, identify });

		// disabling eslint for this section because no matter how it was formatted, eslint wasn't happy.
		const mutation$ = identify
			? this.deviceStartIdentifyMutationGQL
					.mutate({ variables: { startIdentifyId: deviceId } })
					.pipe(map((result) => result.data?.startIdentify.error))
			: this.deviceStopIdentifyMutationGQL
					.mutate({ variables: { stopIdentifyId: deviceId } })
					.pipe(map((result) => result.data?.stopIdentify.error));

		return mutation$.pipe(
			timeout(defaultMutationTimeout),
			map((error) => {
				// If error is null, the request was a success.
				return {
					state: !error ? UpdateState.Done : UpdateState.Error
				};
			}),
			catchError((error: Error) => {
				this.logger.error('toggleIdentify', 'Error', { error });
				return of({ state: UpdateState.Error, error: error.message });
			})
		);
	}

	/**
	 * Set mute for a device.
	 * @param deviceId
	 * @param deviceMute
	 * @returns
	 */
	public setMute(deviceId: string, deviceMute: boolean): Observable<UpdateRequestResponse<string>> {
		this.logger.trace('setMute', 'Setting mute', { deviceId, deviceMute });
		return this.deviceUpdateMutationGQL
			.mutate({
				variables: {
					updates: [
						{
							device: {
								features: {
									audioMute: {
										muted: deviceMute
									}
								},
								id: deviceId
							}
						}
					]
				}
			})
			.pipe(
				timeout(defaultMutationTimeout),
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						this.logger.error('setMute', 'Error', {
							deviceId,
							result
						});
						return { state: UpdateState.Error, error: result.data.updateNodes[0].error?.message };
					}
					this.logger.trace('setMute', 'Done', { deviceId });
					return { state: UpdateState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('setMute', 'Error', { error });
					return of({ state: UpdateState.Error, error: error.message });
				})
			);
	}

	public setDeviceName(deviceId: string, name: string): Observable<UpdateResponse<void, string>> {
		this.logger.trace('setName()', 'Setting name', { deviceId, name });
		return this.deviceUpdateMutationGQL
			.mutate({
				variables: {
					updates: [
						{
							device: {
								features: {
									name: {
										name
									}
								},
								id: deviceId
							}
						}
					]
				}
			})
			.pipe(
				timeout(defaultMutationTimeout),
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						this.logger.error('setName', 'Error', {
							deviceId,
							result
						});
						return { state: UpdateState.Error, error: result.data.updateNodes[0].error?.message };
					}
					this.logger.trace('setName()', 'Done', { deviceId });
					return { state: UpdateState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('setName()', 'Error', { error });
					return of({ state: UpdateState.Error, error: error.message });
				})
			);
	}

	/**
	 * Reboot a device
	 * @param deviceId
	 * @returns
	 */
	public rebootDevice$(deviceId: string): Observable<UpdateResponse<void, string>> {
		this.logger.trace('rebootDevice()', 'Rebooting device', { deviceId });
		return this.deviceRebootMutationGQL.mutate({ variables: { rebootDeviceId: deviceId } }).pipe(
			timeout(defaultMutationTimeout),
			map((result) => {
				if (result.data?.rebootDevice.error) {
					this.logger.error('rebootDevice', 'error', result.data?.rebootDevice.error);
					throw new Error(result.data?.rebootDevice.error.message);
				}
				this.logger.trace('rebootDevice', 'Done', { deviceId });
				return { state: UpdateState.Done };
			}),
			catchError((error: Error) => {
				this.logger.error('rebootDevice', 'Error', { error });
				return of({ state: UpdateState.Error, error: error.message });
			})
		);
	}

	/**
	 * Associate a tag with a set of devices
	 * @param deviceIds
	 * @param tag
	 * @returns
	 */
	public associateTag(deviceIds: string[], tag: string): Observable<AssociateTagMutationResult> {
		this.logger.trace('associateTag', 'AssociateTag', { deviceIds: deviceIds, tag });
		const concurrency = 10;
		const chunkedDevices = chunk(deviceIds, 50);
		const retVal: AssociateTagMutationResult = {
			state: MutationState.Done,
			errorMessage: undefined,
			resultData: []
		};

		return from(chunkedDevices).pipe(
			mergeMap((chunk) => {
				return this.associateTagMutationGQL
					.mutate({
						variables: {
							tagInput: { devices: chunk, tag: tag }
						}
					})
					.pipe(
						timeout(defaultMutationTimeout),
						map((result) => {
							return {
								state: MutationState.Done,
								errorMessage: undefined,
								resultData: result.data?.associateTag ?? []
							};
						}),
						catchError((error: Error) => {
							return of({
								state: MutationState.Error,
								errorMessage: error.message,
								resultData: []
							});
						})
					);
			}, concurrency),
			reduce((acc, curr) => {
				if (curr.state === MutationState.Error) {
					acc.state = MutationState.Error;
					acc.errorMessage = curr.errorMessage;
				}
				acc.resultData = acc.resultData?.concat(curr.resultData);
				return acc;
			}, retVal)
		);
	}

	public dissociateTag(deviceIds: string[], tag: string): Observable<DissociateTagMutationResult> {
		this.logger.trace('dissociateTag', 'DissociateTag', { deviceIds: deviceIds, tag });
		const concurrency = 10;
		const chunkedDevices = chunk(deviceIds, 50);
		const retVal: DissociateTagMutationResult = {
			state: MutationState.Done,
			errorMessage: undefined,
			resultData: []
		};
		return from(chunkedDevices).pipe(
			mergeMap((chunk) => {
				return this.dissociateTagMutationGQL
					.mutate({
						variables: {
							tagInput: { devices: chunk, tag: tag }
						}
					})
					.pipe(
						timeout(defaultMutationTimeout),
						map((r) => ({
							state: MutationState.Done,
							errorMessage: undefined,
							resultData: r.data?.dissociateTag ?? []
						})),
						catchError((error: Error) => {
							return of({ state: MutationState.Error, errorMessage: error.message, resultData: [] });
						})
					);
			}, concurrency),
			reduce((acc, curr) => {
				if (curr.state === MutationState.Error) {
					acc.state = MutationState.Error;
					acc.errorMessage = curr.errorMessage;
				}
				acc.resultData = acc.resultData?.concat(curr.resultData);
				return acc;
			}, retVal)
		);
	}

	public setIotRoomProxy(deviceId: string, enabled: boolean): Observable<UpdateResponse<void, string>> {
		this.logger.trace('setIotRoomProxy', 'Setting IoT Room Proxy feature', { deviceId, enabled });
		return this.deviceUpdateIotRoomProxyMutationGQL.mutate({ variables: { deviceId, enabled } }).pipe(
			map((result) => {
				if (
					result.data?.updateNodes &&
					'error' in result.data.updateNodes[0] &&
					result.data.updateNodes[0].error !== null
				) {
					throw new Error(
						result.data.updateNodes[0].error?.message || 'Failed to update IoT Room Proxy feature'
					);
				}

				this.logger.trace('setIotRoomProxy()', 'Done', { deviceId });
				return { state: UpdateState.Done };
			}),
			catchError((error: Error) => {
				this.logger.error('setIotRoomProxy()', 'Error', { error });
				return of({ state: UpdateState.Error, error: error.message });
			})
		);
	}

	public submitFirmwareUpdateRequests(
		updates: RequestFirmwareUpdateApiInput[]
	): Observable<UpdateResponse<void, string>> {
		this.logger.debug('submitFirmwareUpdateRequests', 'Submitting firmware update requests', {
			totalUpdates: updates.length
		});

		// submit up to 150 updates per batch, with 5 in-progress at a time.
		const batchSize = 150;
		const maxConcurrentBatches = 5;
		const chunkedUpdates = chunk(updates, batchSize);

		return from(chunkedUpdates).pipe(
			mergeMap((updateBatch) => {
				const batchWithHostedRemoved = updateBatch.map(({ isHosted, ...rest }) => ({ ...rest }));
				return this.requestFirmwareUpdates
					.mutate({ variables: { firmwareUpdates: batchWithHostedRemoved } })
					.pipe(
						timeout(defaultMutationTimeout),
						map((result) => {
							if ('data' in result) {
								this.markFirmwareUpdateRequestsAsPending(
									updateBatch,
									result.data?.requestFirmwareUpdates
								);
								return { state: UpdateState.Done };
							}
							throw new Error('error' in result ? result : '');
						}),
						catchError((error: Error) => {
							this.logger.error('submitFirmwareUpdateRequests', 'Error submitting batch', { error });
							return of({ state: UpdateState.Error, error: error.message });
						})
					);
			}, maxConcurrentBatches),
			reduce(
				(acc, curr) => {
					if (curr.state === UpdateState.Error) {
						acc.state = UpdateState.Error;
					}
					return acc;
				},
				{ state: UpdateState.Done } as UpdateResponse<void, string>
			)
		);
	}

	//
	// Auracast Mutations
	//

	public setBluetoothPowerLevel(
		deviceId: string,
		level: DeviceBluetoothPower
	): Observable<UpdateResponse<void, string>> {
		this.logger.trace('setBluetoothPowerLevel', 'Setting Bluetooth power level', { deviceId, level });
		return this.executeMutation(
			'setBluetoothPowerLevel',
			this.setDeviceBluetoothPowerMutationGQL.mutate({
				variables: {
					deviceId,
					level: mapDeviceBluetoothPowerToSysApi(level)
				}
			}),
			(data) => {
				const node = data.updateNodes[0];
				return 'error' in node && node.error !== null ? node.error.message : null;
			}
		);
	}

	public updateAuracastChannelSettings(
		input: UpdateChannelAuracastApiInput
	): Observable<UpdateResponse<void, string>> {
		this.logger.trace('updateAuracastChannelSettings', 'Updating auracast channel settings', input);
		return this.executeMutation(
			'updateAuracastChannelSettings',
			this.updateAuracastConfigurationMutationGQL.mutate({
				variables: {
					input: { id: input.id, enabled: input.enabled, name: input.name, encryption: input.encryption }
				}
			}),
			(data) => data.updateAuracastConfiguration.error?.message ?? null
		);
	}

	// Generic function to execute a mutation and handle common response/error
	private executeMutation<TData>(
		methodName: string,
		mutation$: Observable<{ data?: TData | null }>,
		extractError: (data: TData) => string | null
	): Observable<UpdateResponse<void, string>> {
		return mutation$.pipe(
			timeout(defaultMutationTimeout),
			map((result) => {
				if (result.data != null) {
					const error = extractError(result.data);
					if (error) {
						throw new Error(error);
					}
				}
				this.logger.trace(methodName, 'Done');
				return { state: UpdateState.Done };
			}),
			catchError((error: Error) => {
				this.logger.error(methodName, 'Error', { error });
				return of<UpdateResponse<void, string>>({ state: UpdateState.Error, error: error.message });
			})
		);
	}

	private isFirmwareUpdateRequestFailed(requestState: FirmwareUpdateRequestState | null | undefined): boolean {
		if (requestState === null || requestState === undefined) {
			return true;
		}

		const successStates = [
			FirmwareUpdateRequestState.Pending,
			FirmwareUpdateRequestState.InProgress,
			FirmwareUpdateRequestState.Successful
		];

		return !successStates.includes(requestState);
	}

	// This function exists to help improve the UX when firmware updates are submitted.
	// Instead of waiting for an event from the BE, this will add an entry to the apollo cache so the UI will
	// update as if the request is already pending. W/out this the delay between the Firwareupdate submission
	// and when the UI receives the first update presents a less-than-optimal solution.
	private markFirmwareUpdateRequestsAsPending(
		requests: RequestFirmwareUpdateApiInput[],
		results?: (FirmwareUpdateRequestMutationResponseFragment | null)[]
	): void {
		results?.forEach((result) => {
			if (!result) {
				return;
			}
			const matchedRequest = requests.find(
				(req) =>
					req.id === result.device?.id &&
					req.packageKey === result.packageKey &&
					req.packageVersion === result.packageVersion
			);
			if (!matchedRequest) {
				return;
			}

			// When seeding the cache for a firmwareUpdateRequest, the cache item must contain a fields for the primary
			// and hosted section of the firmwareUpdateRequest feature..  However, for any individual FW Update Request,
			// we only have one or the other. We can't just assume "null" for the part we don't have because we could be
			// overriding a non-null value for it. therefore, we start by reading the feature for the
			// current feature value for this device, update the part for this request, then write the full picture
			// back to the cache.
			const deviceFragment = this.inventoryApolloCache.readDeviceFragment(result.device?.id ?? '');
			const statusFragment: FirmwareUpdateRequestsByStatusFragment = {
				// eslint-disable-next-line @typescript-eslint/naming-convention
				__typename: 'FirmwareUpdateRequestsByStatus',
				current: {
					// eslint-disable-next-line @typescript-eslint/naming-convention
					__typename: 'FirmwareUpdateRequest',
					id: result?.id ?? '',
					device: {
						id: result.device?.id ?? ''
					},
					packageKey: result.packageKey,
					packageVersion: result.packageVersion,
					hosted: matchedRequest.isHosted,
					status: result.status,
					progress: result.progress,
					error: result.error,
					executionStatus: result.executionStatus
				}
			};
			const cacheSeedValue = matchedRequest.isHosted
				? {
						// updating the hosted section, so retain any prior section for the primary
						// if there was no prior primary value, set it to null
						primary: deviceFragment?.features.firmwareUpdateRequests?.primary ?? {
							// eslint-disable-next-line @typescript-eslint/naming-convention
							__typename: 'FirmwareUpdateRequestsByStatus',
							current: null
						},
						hosted: statusFragment
					}
				: {
						// updating the primary section, so retain any prior section for the hosted
						// if there was no prior hosted value, set it to null
						primary: statusFragment,
						hosted: deviceFragment?.features.firmwareUpdateRequests?.hosted ?? {
							// eslint-disable-next-line @typescript-eslint/naming-convention
							__typename: 'FirmwareUpdateRequestsByStatus',
							current: null
						}
					};
			this.inventoryApolloCache.updateFeature(matchedRequest.id, 'firmwareUpdateRequests', {
				// eslint-disable-next-line @typescript-eslint/naming-convention
				__typename: 'DeviceFirmwareUpdateRequests',
				...cacheSeedValue
			});
		});
	}
}
